import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Alert, Row, Col, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUnlock, faFileExport, faClipboard, faTrash, faExclamationTriangle, faCheck } from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';
import { useAuth } from '../../contexts/AuthContext';

const CredentialVault = () => {
  const [credentials, setCredentials] = useState([]);
  const [showDecryptModal, setShowDecryptModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [decryptedValue, setDecryptedValue] = useState(null);
  const [exportFormat, setExportFormat] = useState('hashcat');
  const [exportedValue, setExportedValue] = useState(null);
  const [filter, setFilter] = useState({ type: '', username: '', domain: '' });
  const { user } = useAuth();

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    try {
      const response = await c2Service.getCredentials(filter);
      setCredentials(response.data || []);
    } catch (error) {
      console.error('Failed to load credentials:', error);
    }
  };

  const handleDecrypt = async () => {
    if (!window.confirm('Decrypt this credential?\n\nThis action will be logged in the audit trail.')) {
      return;
    }

    try {
      const response = await c2Service.decryptCredential(selectedCredential._id);
      setDecryptedValue(response.data.value);
      // Reload to update access log
      loadCredentials();
    } catch (error) {
      console.error('Failed to decrypt credential:', error);
      alert('Failed to decrypt: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleExport = async () => {
    try {
      const response = await c2Service.exportCredential(selectedCredential._id, exportFormat);
      setExportedValue(response.data.value);
    } catch (error) {
      console.error('Failed to export credential:', error);
      alert('Failed to export: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (credentialId) => {
    if (!window.confirm('Delete this credential?\n\nThis action cannot be undone.')) {
      return;
    }

    try {
      await c2Service.deleteCredential(credentialId);
      loadCredentials();
    } catch (error) {
      console.error('Failed to delete credential:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      plaintext_password: { bg: 'danger', label: 'Password' },
      ntlm_hash: { bg: 'warning', label: 'NTLM Hash' },
      kerberos_ticket: { bg: 'info', label: 'Kerberos' },
      ssh_private_key: { bg: 'primary', label: 'SSH Key' },
      api_token: { bg: 'success', label: 'API Token' },
      browser_cookie: { bg: 'secondary', label: 'Cookie' },
      lsass_dump: { bg: 'dark', label: 'LSASS Dump' }
    };
    const config = typeConfig[type] || { bg: 'secondary', label: type };
    return <Badge bg={config.bg}>{config.label}</Badge>;
  };

  const getExpiryBadge = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const daysLeft = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) return <Badge bg="dark">Expired</Badge>;
    if (daysLeft < 7) return <Badge bg="danger">Expires in {daysLeft}d</Badge>;
    if (daysLeft < 30) return <Badge bg="warning">Expires in {daysLeft}d</Badge>;
    return <Badge bg="success">Expires in {daysLeft}d</Badge>;
  };

  const canDecrypt = user?.permissions?.includes('c2.credentials.decrypt');
  const canExport = user?.permissions?.includes('c2.credentials.export');
  const canDelete = user?.permissions?.includes('c2.credentials.delete');

  return (
    <>
      <Card className="mb-3">
        <Card.Header style={{ padding: '0.75rem 1rem', backgroundColor: '#374151', borderBottom: '1px solid #4b5563' }}>
          <strong style={{ color: '#fff' }}>Filters</strong>
        </Card.Header>
        <Card.Body>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <Form.Label style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '0.5rem', display: 'block' }}>Type</Form.Label>
              <Form.Select
                value={filter.type}
                onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                style={{ width: '100%', backgroundColor: '#1f2937', color: '#e5e7eb', border: '1px solid #4b5563' }}
              >
                <option value="">All types</option>
                <option value="plaintext_password">Password</option>
                <option value="ntlm_hash">NTLM Hash</option>
                <option value="kerberos_ticket">Kerberos Ticket</option>
                <option value="ssh_private_key">SSH Key</option>
                <option value="api_token">API Token</option>
                <option value="browser_cookie">Browser Cookie</option>
                <option value="lsass_dump">LSASS Dump</option>
              </Form.Select>
            </div>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <Form.Label style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '0.5rem', display: 'block' }}>Username</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search username..."
                value={filter.username}
                onChange={(e) => setFilter({ ...filter, username: e.target.value })}
                style={{ width: '100%', backgroundColor: '#1f2937', color: '#e5e7eb', border: '1px solid #4b5563' }}
              />
            </div>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <Form.Label style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '0.5rem', display: 'block' }}>Domain</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search domain..."
                value={filter.domain}
                onChange={(e) => setFilter({ ...filter, domain: e.target.value })}
                style={{ width: '100%', backgroundColor: '#1f2937', color: '#e5e7eb', border: '1px solid #4b5563' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
            <Button size="sm" onClick={loadCredentials}>
              <FontAwesomeIcon icon={faSearch} /> Apply Filters
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setFilter({ type: '', username: '', domain: '' });
                loadCredentials();
              }}
            >
              Clear
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', backgroundColor: '#374151', borderBottom: '1px solid #4b5563' }}>
          <strong style={{ color: '#fff' }}>Credentials ({credentials?.length || 0})</strong>
          <div style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', border: '1px solid #f59e0b' }}>
            <small>
              <strong><FontAwesomeIcon icon={faExclamationTriangle} /> Security Notice:</strong> All credential access is logged and audited
            </small>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
            <thead>
              <tr style={{ backgroundColor: '#374151', borderBottom: '1px solid #4b5563' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Type</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Username</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Domain/Hostname</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Source</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Beacon</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Captured</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Expiry</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(credentials) ? credentials : []).map(cred => (
                <tr key={cred._id} style={{ backgroundColor: '#1f2937', borderBottom: '1px solid #374151' }}>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    {getTypeBadge(cred.type)}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    <strong>{cred.metadata?.username || 'N/A'}</strong>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    {cred.metadata?.domain || cred.metadata?.hostname || 'N/A'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    <Badge bg="secondary">{cred.metadata?.source || 'N/A'}</Badge>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    <code className="small">{cred.beacon?.beaconId || 'N/A'}</code>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                    <small>
                      {new Date(cred.createdAt).toLocaleString()}
                    </small>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    {getExpiryBadge(cred.expiresAt)}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '0.25rem' }}>
                      {canDecrypt && (
                        <button
                          style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
                          onClick={() => {
                            setSelectedCredential(cred);
                            setDecryptedValue(null);
                            setShowDecryptModal(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faUnlock} /> Decrypt
                        </button>
                      )}
                      {canExport && (
                        <button
                          style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#0ea5e9', color: '#fff', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
                          onClick={() => {
                            setSelectedCredential(cred);
                            setExportedValue(null);
                            setShowExportModal(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faFileExport} /> Export
                        </button>
                      )}
                      <button
                        style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: 'transparent', color: '#9ca3af', border: '1px solid #4b5563', borderRadius: '0.25rem', cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedCredential(cred);
                          setShowAuditModal(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faClipboard} /> Audit Log
                      </button>
                      {canDelete && (
                        <button
                          style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
                          onClick={() => handleDelete(cred._id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {credentials.length === 0 && (
            <div className="text-center text-muted py-5">
              <p className="mb-0">No credentials found</p>
              <small>Credentials will appear here after harvesting</small>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Decrypt Modal */}
      <Modal show={showDecryptModal} onHide={() => setShowDecryptModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Decrypt Credential</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCredential && (
            <>
              <Alert variant="danger">
                <strong><FontAwesomeIcon icon={faExclamationTriangle} /> Security Warning</strong>
                <p className="mb-0 mt-2">
                  Decrypting this credential will log your user ID, timestamp, and IP address.
                  Only decrypt if you have a legitimate operational need.
                </p>
              </Alert>

              <Table size="sm" bordered>
                <tbody>
                  <tr>
                    <td><strong>Type:</strong></td>
                    <td>{getTypeBadge(selectedCredential.type)}</td>
                  </tr>
                  <tr>
                    <td><strong>Username:</strong></td>
                    <td>{selectedCredential.metadata?.username}</td>
                  </tr>
                  <tr>
                    <td><strong>Domain:</strong></td>
                    <td>{selectedCredential.metadata?.domain || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Source:</strong></td>
                    <td>{selectedCredential.metadata?.source}</td>
                  </tr>
                  <tr>
                    <td><strong>Captured:</strong></td>
                    <td>{new Date(selectedCredential.createdAt).toLocaleString()}</td>
                  </tr>
                </tbody>
              </Table>

              {decryptedValue ? (
                <>
                  <Alert variant="success">
                    <strong><FontAwesomeIcon icon={faCheck} /> Decrypted Successfully</strong>
                  </Alert>
                  <Form.Group>
                    <Form.Label>Credential Value:</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={decryptedValue}
                      readOnly
                      className="font-monospace"
                    />
                  </Form.Group>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mt-2"
                    onClick={() => copyToClipboard(decryptedValue)}
                  >
                    <FontAwesomeIcon icon={faClipboard} /> Copy to Clipboard
                  </Button>
                  <Alert variant="warning" className="mt-3 mb-0">
                    <small>
                      <strong>Note:</strong> This value will not be shown again. Copy it now if needed.
                    </small>
                  </Alert>
                </>
              ) : (
                <Alert variant="info">
                  Click "Decrypt Now" to reveal the credential value.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDecryptModal(false)}>
            Close
          </Button>
          {!decryptedValue && (
            <Button variant="warning" onClick={handleDecrypt}>
              <FontAwesomeIcon icon={faUnlock} /> Decrypt Now
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Export Modal */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Export Credential</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCredential && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Export Format</Form.Label>
                <Form.Select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                >
                  <option value="hashcat">Hashcat (username:hash)</option>
                  <option value="john">John the Ripper (username:domain:hash)</option>
                  <option value="mimikatz">Mimikatz (sekurlsa::pth command)</option>
                  <option value="plain">Plain text</option>
                </Form.Select>
              </Form.Group>

              {exportedValue ? (
                <>
                  <Form.Group>
                    <Form.Label>Exported Value:</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={exportedValue}
                      readOnly
                      className="font-monospace"
                    />
                  </Form.Group>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mt-2"
                    onClick={() => copyToClipboard(exportedValue)}
                  >
                    <FontAwesomeIcon icon={faClipboard} /> Copy to Clipboard
                  </Button>
                </>
              ) : (
                <Alert variant="info">
                  Click "Export" to generate the formatted credential.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Close
          </Button>
          {!exportedValue && (
            <Button variant="primary" onClick={handleExport}>
              <FontAwesomeIcon icon={faFileExport} /> Export
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Audit Log Modal */}
      <Modal show={showAuditModal} onHide={() => setShowAuditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Audit Log</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCredential && (
            <>
              <h6>Credential Information</h6>
              <Table size="sm" bordered className="mb-4">
                <tbody>
                  <tr>
                    <td><strong>Username:</strong></td>
                    <td>{selectedCredential.metadata?.username}</td>
                  </tr>
                  <tr>
                    <td><strong>Domain:</strong></td>
                    <td>{selectedCredential.metadata?.domain || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Type:</strong></td>
                    <td>{getTypeBadge(selectedCredential.type)}</td>
                  </tr>
                </tbody>
              </Table>

              <h6>Access History</h6>
              {selectedCredential.accessLog && selectedCredential.accessLog.length > 0 ? (
                <Table size="sm" hover>
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>User</th>
                      <th>Timestamp</th>
                      <th>IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCredential.accessLog.map((log, idx) => (
                      <tr key={idx}>
                        <td>
                          <Badge bg={
                            log.action === 'decrypted' ? 'warning' :
                            log.action === 'exported' ? 'info' :
                            log.action === 'deleted' ? 'danger' : 'secondary'
                          }>
                            {log.action}
                          </Badge>
                        </td>
                        <td>{log.user?.username || log.user || 'System'}</td>
                        <td>
                          <small>{new Date(log.accessedAt).toLocaleString()}</small>
                        </td>
                        <td>
                          <small className="text-muted">{log.ipAddress || 'N/A'}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="info">
                  No access history yet. This credential has not been decrypted or exported.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAuditModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CredentialVault;
